/**
 * Authentication Mode Manager
 *
 * Manages the three operating modes of Phrasewright:
 * - NO_ACCOUNT: Local-only, no authentication required (default)
 * - ACCOUNT_LOCAL: Authenticated but cloud sync disabled
 * - ACCOUNT_CLOUD: Authenticated with cloud sync enabled
 *
 * Task 157: Privacy-first dual-mode architecture
 */

// Simple EventEmitter for browser compatibility
class EventEmitter {
  private events: Record<string, Array<(...args: any[]) => void>> = {};

  on(event: string, listener: (...args: any[]) => void): this {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(listener);
    return this;
  }

  off(event: string, listener: (...args: any[]) => void): this {
    if (this.events[event]) {
      this.events[event] = this.events[event].filter((l) => l !== listener);
    }
    return this;
  }

  emit(event: string, ...args: any[]): boolean {
    if (this.events[event]) {
      this.events[event].forEach((listener) => listener(...args));
      return true;
    }
    return false;
  }

  removeAllListeners(event?: string): this {
    if (event) {
      delete this.events[event];
    } else {
      this.events = {};
    }
    return this;
  }
}

export enum AuthMode {
  NO_ACCOUNT = 'NO_ACCOUNT',
  ACCOUNT_LOCAL = 'ACCOUNT_LOCAL',
  ACCOUNT_CLOUD = 'ACCOUNT_CLOUD',
}

export interface AuthModeConfig {
  mode: AuthMode;
  cloudSyncEnabled: boolean;
  lastModeChange: string;
  userId?: string;
  workspaceId?: string;
  dataResidency?: 'US' | 'EU' | 'APAC';
}

export interface ModeChangeEvent {
  previousMode: AuthMode;
  newMode: AuthMode;
  timestamp: string;
  preserveData: boolean;
}

const AUTH_MODE_KEY = 'phrasewright.authMode';
const AUTH_CONFIG_KEY = 'phrasewright.authConfig';

export class AuthModeManager extends EventEmitter {
  private static instance: AuthModeManager;
  private currentMode: AuthMode = AuthMode.NO_ACCOUNT;
  private config: AuthModeConfig;

  private constructor() {
    super();
    this.config = this.loadConfig();
    this.currentMode = this.config.mode;
  }

  static getInstance(): AuthModeManager {
    if (!AuthModeManager.instance) {
      AuthModeManager.instance = new AuthModeManager();
    }
    return AuthModeManager.instance;
  }

  /**
   * Load configuration from localStorage
   */
  private loadConfig(): AuthModeConfig {
    try {
      const stored = localStorage.getItem(AUTH_CONFIG_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.warn('Failed to load auth config, using defaults', error);
    }

    // Default configuration - privacy first
    return {
      mode: AuthMode.NO_ACCOUNT,
      cloudSyncEnabled: false,
      lastModeChange: new Date().toISOString(),
    };
  }

  /**
   * Save configuration to localStorage
   */
  private saveConfig(): void {
    try {
      localStorage.setItem(AUTH_CONFIG_KEY, JSON.stringify(this.config));
      localStorage.setItem(AUTH_MODE_KEY, this.currentMode);
    } catch (error) {
      console.error('Failed to save auth config', error);
    }
  }

  /**
   * Get current authentication mode
   */
  getMode(): AuthMode {
    return this.currentMode;
  }

  /**
   * Get full configuration
   */
  getConfig(): Readonly<AuthModeConfig> {
    return { ...this.config };
  }

  /**
   * Check if app is in no-account mode
   */
  isNoAccountMode(): boolean {
    return this.currentMode === AuthMode.NO_ACCOUNT;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return (
      this.currentMode === AuthMode.ACCOUNT_LOCAL ||
      this.currentMode === AuthMode.ACCOUNT_CLOUD
    );
  }

  /**
   * Check if cloud sync is enabled
   */
  isCloudSyncEnabled(): boolean {
    return (
      this.currentMode === AuthMode.ACCOUNT_CLOUD &&
      this.config.cloudSyncEnabled
    );
  }

  /**
   * Detect authentication mode on startup
   */
  async detectMode(): Promise<AuthMode> {
    // Check for auth token in cookies
    const hasAuthToken = document.cookie.includes('phrasewright_token');

    if (!hasAuthToken) {
      // No authentication - stay in no-account mode
      this.setMode(AuthMode.NO_ACCOUNT);
      return AuthMode.NO_ACCOUNT;
    }

    // Has authentication - check cloud sync preference
    if (this.config.cloudSyncEnabled) {
      this.setMode(AuthMode.ACCOUNT_CLOUD);
      return AuthMode.ACCOUNT_CLOUD;
    } else {
      this.setMode(AuthMode.ACCOUNT_LOCAL);
      return AuthMode.ACCOUNT_LOCAL;
    }
  }

  /**
   * Switch to a different authentication mode
   */
  async setMode(
    newMode: AuthMode,
    options: {
      preserveData?: boolean;
      userId?: string;
      workspaceId?: string;
    } = {}
  ): Promise<void> {
    const previousMode = this.currentMode;
    const preserveData = options.preserveData ?? true;

    // Update configuration
    this.currentMode = newMode;
    this.config.mode = newMode;
    this.config.lastModeChange = new Date().toISOString();

    if (options.userId) {
      this.config.userId = options.userId;
    }
    if (options.workspaceId) {
      this.config.workspaceId = options.workspaceId;
    }

    // Update cloud sync setting based on mode
    if (newMode === AuthMode.NO_ACCOUNT) {
      this.config.cloudSyncEnabled = false;
      delete this.config.userId;
      delete this.config.workspaceId;
    } else if (newMode === AuthMode.ACCOUNT_LOCAL) {
      this.config.cloudSyncEnabled = false;
    }
    // ACCOUNT_CLOUD mode doesn't automatically enable sync - user must opt-in

    this.saveConfig();

    // Emit mode change event
    const event: ModeChangeEvent = {
      previousMode,
      newMode,
      timestamp: this.config.lastModeChange,
      preserveData,
    };
    this.emit('modeChanged', event);
  }

  /**
   * Enable cloud sync (requires authenticated mode)
   */
  async enableCloudSync(
    dataResidency?: 'US' | 'EU' | 'APAC'
  ): Promise<boolean> {
    if (!this.isAuthenticated()) {
      console.warn('Cannot enable cloud sync without authentication');
      return false;
    }

    // Show explicit consent dialog (handled by UI)
    const consent = await this.requestCloudSyncConsent();
    if (!consent) {
      return false;
    }

    this.config.cloudSyncEnabled = true;
    this.config.dataResidency = dataResidency || 'US';
    this.currentMode = AuthMode.ACCOUNT_CLOUD;
    this.config.mode = AuthMode.ACCOUNT_CLOUD;
    this.saveConfig();

    this.emit('cloudSyncEnabled', {
      timestamp: new Date().toISOString(),
      dataResidency: this.config.dataResidency,
    });

    return true;
  }

  /**
   * Disable cloud sync (keeps authentication)
   */
  async disableCloudSync(): Promise<void> {
    if (!this.isAuthenticated()) {
      return;
    }

    this.config.cloudSyncEnabled = false;
    this.currentMode = AuthMode.ACCOUNT_LOCAL;
    this.config.mode = AuthMode.ACCOUNT_LOCAL;
    this.saveConfig();

    this.emit('cloudSyncDisabled', {
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Sign out and return to no-account mode
   */
  async signOut(options: { preserveData?: boolean } = {}): Promise<void> {
    const preserveData = options.preserveData ?? true;

    // Clear authentication
    document.cookie =
      'phrasewright_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

    // Return to no-account mode
    await this.setMode(AuthMode.NO_ACCOUNT, { preserveData });

    this.emit('signedOut', {
      timestamp: new Date().toISOString(),
      preserveData,
    });
  }

  /**
   * Request explicit consent for cloud sync
   * This will be overridden by the UI layer to show actual dialog
   */
  private async requestCloudSyncConsent(): Promise<boolean> {
    // Default implementation - UI will override
    return false;
  }

  /**
   * Get privacy-compliant mode description
   */
  getModeDescription(): string {
    switch (this.currentMode) {
      case AuthMode.NO_ACCOUNT:
        return 'All data stored locally on your device. No account required.';
      case AuthMode.ACCOUNT_LOCAL:
        return 'Signed in, but all data remains on your device. Cloud sync disabled.';
      case AuthMode.ACCOUNT_CLOUD:
        return this.config.cloudSyncEnabled
          ? 'Signed in with cloud sync enabled. Data synced across devices.'
          : 'Signed in with cloud sync available but not enabled.';
    }
  }

  /**
   * Get storage location for UI display
   */
  getStorageLocation(): string {
    if (
      this.currentMode === AuthMode.NO_ACCOUNT ||
      !this.config.cloudSyncEnabled
    ) {
      return 'Local Device Only';
    }
    return `Cloud (${this.config.dataResidency || 'US'})`;
  }

  /**
   * Check if migration is available
   */
  canMigrate(targetMode: AuthMode): boolean {
    // Can always migrate from no-account to account
    if (
      this.currentMode === AuthMode.NO_ACCOUNT &&
      targetMode !== AuthMode.NO_ACCOUNT
    ) {
      return true;
    }

    // Can switch between account modes
    if (this.isAuthenticated() && targetMode !== AuthMode.NO_ACCOUNT) {
      return true;
    }

    // Can return to no-account mode
    if (targetMode === AuthMode.NO_ACCOUNT) {
      return true;
    }

    return false;
  }
}
